import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import AuthGuard from '../components/AuthGuard'
import apiService from '../services/api'

const DiscoverCommunities = () => {
  return (
    <AuthGuard
      requireAuth={true}
      title="কমিউনিটি খুঁজুন"
      description="পাবলিক কমিউনিটি খুঁজুন এবং যোগ দিন।"
    >
      {({ user, logout, openAuth }) => <DiscoverCommunitiesContent user={user} logout={logout} openAuth={openAuth} />}
    </AuthGuard>
  )
}

const DiscoverCommunitiesContent = ({ user, logout, openAuth }) => {
  const navigate = useNavigate()
  const [communities, setCommunities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    loadCommunities()
  }, [])

  const loadCommunities = async (query = '') => {
    try {
      setSearching(true)
      const data = await apiService.searchPublicCommunities(query)
      setCommunities(data.communities || [])
    } catch (err) {
      console.error('Failed to load communities:', err)
      setError(err.message)
    } finally {
      setLoading(false)
      setSearching(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    loadCommunities(searchQuery)
  }

  const handleJoinCommunity = async (communityId, communityName) => {
    if (!confirm(`${communityName} কমিউনিটিতে যোগ দিতে চান?`)) return

    try {
      const result = await apiService.joinPublicCommunity(communityId)
      alert(result.message)

      // Reload communities to update join status
      loadCommunities(searchQuery)
    } catch (err) {
      console.error('Failed to join community:', err)
      alert(err.message)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 flex flex-col">
      <Navbar user={user} logout={logout} openAuth={openAuth} />

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                কমিউনিটি খুঁজুন
              </h1>
              <p className="text-gray-600">
                পাবলিক কমিউনিটি খুঁজুন এবং সরাসরি যোগ দিন
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/communities')}
              className="flex items-center space-x-2"
            >
              <Icon name="ArrowLeft" className="w-4 h-4" />
              <span>ফিরে যান</span>
            </Button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex space-x-2">
              <div className="relative flex-1">
                <Icon name="Search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="কমিউনিটি খুঁজুন (নাম বা বর্ণনা দিয়ে)..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <Button
                type="submit"
                disabled={searching}
                className="px-6"
              >
                {searching ? 'খুঁজছি...' : 'খুঁজুন'}
              </Button>
            </div>
          </form>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground mt-4">লোড হচ্ছে...</p>
            </div>
          ) : error ? (
            <Card className="p-6 text-center">
              <Icon name="AlertCircle" className="w-12 h-12 text-destructive mx-auto mb-2" />
              <p className="text-destructive">Error: {error}</p>
            </Card>
          ) : communities.length === 0 ? (
            <Card className="p-8 text-center">
              <Icon name="Search" className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">কোনো কমিউনিটি পাওয়া যায়নি</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'আপনার সার্চের জন্য কোনো পাবলিক কমিউনিটি পাওয়া যায়নি।' : 'কোনো পাবলিক কমিউনিটি এখনও তৈরি হয়নি।'}
              </p>
            </Card>
          ) : (
            <>
              <div className="mb-4 text-sm text-muted-foreground">
                {communities.length} টি কমিউনিটি পাওয়া গেছে
                {searchQuery && ` "${searchQuery}" এর জন্য`}
              </div>

              <div className="grid gap-4">
                {communities.map((community) => (
                  <Card key={community.id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="Users" className="w-5 h-5 text-primary" />
                          <h3 className="text-xl font-semibold text-gray-900">
                            {community.name}
                          </h3>
                        </div>

                        {community.description && (
                          <p className="text-gray-600 mb-3">
                            {community.description}
                          </p>
                        )}

                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Icon name="User" className="w-4 h-4" />
                            <span>{community.creator_name}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="Users" className="w-4 h-4" />
                            <span>{community.member_count} সদস্য</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Icon name="Calendar" className="w-4 h-4" />
                            <span>{new Date(community.created_date).toLocaleDateString('bn-BD')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4">
                        {community.is_member ? (
                          <div className="flex items-center space-x-2 text-green-600 bg-green-50 px-4 py-2 rounded-lg">
                            <Icon name="Check" className="w-5 h-5" />
                            <span className="font-medium">যোগ দিয়েছেন</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleJoinCommunity(community.id, community.name)}
                            className="whitespace-nowrap"
                          >
                            <Icon name="UserPlus" className="w-4 h-4 mr-2" />
                            যোগ দিন
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default DiscoverCommunities
